/**
 * PlanGate: shows a page only when the store's plan includes it, and an upgrade prompt otherwise.
 * Where the page talks to the server, the server refuses the same requests
 * (requirePlan in backend/src/middleware/entitlements.ts), so this is the explanation, not the lock.
 */

import type { ReactNode } from 'react'
import PageHeader from '../layout/PageHeader'
import { UpgradePrompt } from './UpgradePrompt'
import { useSubscription, type Capability } from '../../hooks/useSubscription'
import styles from './PlanGate.module.css'

export interface PlanGateProps {
  capability: Capability
  /** Page title shown above the prompt */
  title: string
  children: ReactNode
}

export const PlanGate: React.FC<PlanGateProps> = ({ capability, title, children }) => {
  const { can, requiredTierFor, tierName } = useSubscription()
  if (can(capability)) return <>{children}</>

  const required = requiredTierFor(capability)
  const availability = required === 'enterprise' ? 'on Enterprise' : `on ${tierName(required)} and above`
  return (
    <div className={styles.container}>
      <PageHeader title={title} subtitle={`${title} is available ${availability}`} />
      <UpgradePrompt capability={capability} source={`plan-gate-${capability}`} />
    </div>
  )
}
